import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useOffline } from '../context/OfflineContext';
import { 
  Plus, 
  Search, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  ArrowRight,
  Loader2,
  Filter,
  WifiOff,
  XCircle,
  FolderOpen,
  List,
  MapPin,
  ChevronDown,
  ChevronRight,
  Trash2,
  Download,
  Building2,
  Briefcase
} from 'lucide-react';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft', icon: FileText },
  submitted: { label: 'Submitted', class: 'status-submitted', icon: Clock },
  returned: { label: 'Returned', class: 'status-returned', icon: AlertTriangle },
  approved: { label: 'Approved', class: 'status-approved', icon: CheckCircle },
  rejected: { label: 'Rejected', class: 'status-rejected', icon: XCircle },
};

// Trade Folder Card Component for folder view
const TradeFolderCard = ({ trade, forms, isAdminOrManager, navigate, statusConfig }) => {
  const [expanded, setExpanded] = React.useState(true);
  
  return (
    <Card className="overflow-hidden">
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors py-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
            <FolderOpen className="h-5 w-5 text-primary" />
            <CardTitle className="font-heading text-lg">{trade}</CardTitle>
            <Badge variant="secondary" className="ml-2">
              {forms.length} {forms.length === 1 ? 'form' : 'forms'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 pb-4">
          <div className="space-y-3">
            {forms.map(form => {
              const config = statusConfig[form.status];
              const StatusIcon = config.icon;
              const canEdit = isAdminOrManager() || (form.status === 'draft' || form.status === 'returned');
              
              return (
                <div
                  key={form.form_id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(canEdit ? `/forms/${form.form_id}/edit` : `/forms/${form.form_id}`)}
                  data-testid={`folder-form-${form.form_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{form.company_name || form.project_name}</span>
                      <Badge className={config.class} variant="outline">
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {form.location && <span className="mr-3">{form.location}</span>}
                      <span>by {form.created_by_name}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Site Folder Card Component for site-based view
const SiteFolderCard = ({ site, forms, isAdminOrManager, navigate, statusConfig, handleDeleteClick }) => {
  const [expanded, setExpanded] = React.useState(true);
  const approvedCount = forms.filter(f => f.status === 'approved').length;
  
  return (
    <Card className="overflow-hidden">
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors py-4 bg-blue-50/50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
            <Building2 className="h-5 w-5 text-blue-600" />
            <CardTitle className="font-heading text-lg">{site}</CardTitle>
            <Badge variant="secondary" className="ml-2">
              {forms.length} {forms.length === 1 ? 'form' : 'forms'}
            </Badge>
            {approvedCount > 0 && (
              <Badge className="bg-green-100 text-green-700">
                {approvedCount} approved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 pb-4">
          <div className="space-y-3">
            {forms.map(form => {
              const config = statusConfig[form.status];
              const StatusIcon = config.icon;
              const canEdit = isAdminOrManager() || (form.status === 'draft' || form.status === 'returned');
              
              return (
                <div
                  key={form.form_id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(canEdit ? `/forms/${form.form_id}/edit` : `/forms/${form.form_id}`)}
                  data-testid={`site-form-${form.form_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{form.company_name || form.project_name}</span>
                      <Badge className={config.class} variant="outline">
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      <span className="mr-3">{form.trade_type}</span>
                      <span>by {form.created_by_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdminOrManager() && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDeleteClick(e, form)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default function Dashboard() {
  const { user, isAdmin, isAdminOrManager } = useAuth();
  const { isOnline, cacheForms, getCachedForms } = useOffline();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tradeFilter, setTradeFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [viewMode, setViewMode] = useState('sites'); // 'list', 'trades', or 'sites'
  const [offlineMode, setOfflineMode] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingApproved, setDownloadingApproved] = useState(false);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchForms = useCallback(async () => {
    try {
      if (isOnline) {
        const response = await axios.get(`${API}/forms`);
        setForms(response.data);
        // Cache forms for offline use
        await cacheForms(response.data);
        setOfflineMode(false);
      } else {
        // Load from cache when offline
        const cachedForms = await getCachedForms();
        setForms(cachedForms);
        setOfflineMode(true);
      }
    } catch (error) {
      // Try to load from cache on error
      try {
        const cachedForms = await getCachedForms();
        if (cachedForms.length > 0) {
          setForms(cachedForms);
          setOfflineMode(true);
          toast.info('Loaded forms from offline cache');
        } else {
          toast.error('Failed to load forms');
        }
      } catch (cacheError) {
        toast.error('Failed to load forms');
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, cacheForms, getCachedForms]);

  const filterForms = useCallback(() => {
    let result = [...forms];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(form =>
        form.company_name || form.project_name.toLowerCase().includes(term) ||
        form.trade_type.toLowerCase().includes(term) ||
        form.created_by_name.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(form => form.status === statusFilter);
    }

    if (tradeFilter !== 'all') {
      result = result.filter(form => form.trade_type === tradeFilter);
    }

    if (siteFilter !== 'all') {
      result = result.filter(form => form.location === siteFilter);
    }

    result.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    setFilteredForms(result);
  }, [forms, searchTerm, statusFilter, tradeFilter, siteFilter]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  useEffect(() => {
    filterForms();
  }, [filterForms]);

  const handleDeleteClick = (e, form) => {
    e.stopPropagation(); // Prevent navigating to form
    setFormToDelete(form);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!formToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/forms/${formToDelete.form_id}`);
      toast.success('Form deleted successfully');
      setForms(forms.filter(f => f.form_id !== formToDelete.form_id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setFormToDelete(null);
    }
  };

  const getUniqueTradeTypes = () => {
    const trades = [...new Set(forms.map(form => form.trade_type))];
    return trades.sort();
  };

  const getUniqueSites = () => {
    const sites = [...new Set(forms.map(form => form.location).filter(Boolean))];
    return sites.sort();
  };

  // Group forms by trade for folder view
  const getFormsByTrade = () => {
    const grouped = {};
    filteredForms.forEach(form => {
      if (!grouped[form.trade_type]) {
        grouped[form.trade_type] = [];
      }
      grouped[form.trade_type].push(form);
    });
    return grouped;
  };

  // Group forms by construction site
  const getFormsBySite = () => {
    const grouped = {};
    filteredForms.forEach(form => {
      const site = form.site_name || form.location || 'Unassigned';
      if (!grouped[site]) {
        grouped[site] = [];
      }
      grouped[site].push(form);
    });
    return grouped;
  };

  // Bulk download functions
  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const formsToDownload = siteFilter !== 'all' 
        ? filteredForms 
        : forms;
      
      await generateBulkPDF(formsToDownload, 'All_Forms');
      toast.success(`Downloaded ${formsToDownload.length} forms`);
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadApproved = async () => {
    setDownloadingApproved(true);
    try {
      const approvedForms = (siteFilter !== 'all' ? filteredForms : forms)
        .filter(f => f.status === 'approved');
      
      if (approvedForms.length === 0) {
        toast.error('No approved forms to download');
        setDownloadingApproved(false);
        return;
      }
      
      await generateBulkPDF(approvedForms, 'Approved_Forms');
      toast.success(`Downloaded ${approvedForms.length} approved forms`);
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingApproved(false);
    }
  };

  const generateBulkPDF = async (formsToExport, filename) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 15;
    
    for (let formIdx = 0; formIdx < formsToExport.length; formIdx++) {
      const form = formsToExport[formIdx];
      
      if (formIdx > 0) pdf.addPage();
      
      let yPos = 20;
      
      // Header
      pdf.setFontSize(20);
      pdf.setTextColor(0, 86, 210);
      pdf.text('QA Builder Pro', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(14);
      pdf.setTextColor(51, 65, 85);
      pdf.text(form.company_name || form.project_name, margin, yPos);
      yPos += 6;
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`${form.trade_type} | ${form.status.toUpperCase()} | ${form.site_name || form.location || 'N/A'}`, margin, yPos);
      yPos += 8;
      
      pdf.text(`Created by: ${form.created_by_name} | Date: ${form.individual_date || 'N/A'}`, margin, yPos);
      yPos += 10;
      
      // Checklist summary if exists
      if (form.checklist?.items) {
        pdf.setFontSize(11);
        pdf.setTextColor(0, 86, 210);
        pdf.text('Checklist:', margin, yPos);
        yPos += 5;
        
        pdf.setFontSize(8);
        pdf.setTextColor(51, 65, 85);
        form.checklist.items.slice(0, 10).forEach((item, idx) => {
          if (item.requirement) {
            pdf.text(`${idx + 1}. ${item.requirement}: ${item.status || '-'}`, margin, yPos);
            yPos += 4;
          }
        });
        yPos += 5;
      }
      
      // Notes
      if (form.notes) {
        pdf.setFontSize(9);
        pdf.text(`Notes: ${form.notes.substring(0, 200)}${form.notes.length > 200 ? '...' : ''}`, margin, yPos);
      }
    }
    
    pdf.save(`${filename}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  const getStatusCounts = () => {
    return {
      all: forms.length,
      draft: forms.filter(f => f.status === 'draft').length,
      submitted: forms.filter(f => f.status === 'submitted').length,
      returned: forms.filter(f => f.status === 'returned').length,
      approved: forms.filter(f => f.status === 'approved').length,
      rejected: forms.filter(f => f.status === 'rejected').length,
    };
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              Welcome, {user?.name}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdminOrManager() ? 'Manage and review QA forms' : 'Your QA forms and submissions'}
            </p>
            {user?.site_name && (
              <p className="text-sm text-primary mt-1 flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {user.site_name} {user.site_location && `- ${user.site_location}`}
              </p>
            )}
            {offlineMode && (
              <div className="flex items-center gap-2 mt-2 text-amber-600">
                <WifiOff className="h-4 w-4" />
                <span className="text-sm">Viewing cached forms (offline)</span>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Bulk Download Buttons */}
            <Button 
              variant="outline" 
              onClick={handleDownloadAll}
              disabled={downloadingAll || forms.length === 0}
              data-testid="download-all-btn"
            >
              {downloadingAll ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download All
            </Button>
            <Button 
              variant="outline" 
              onClick={handleDownloadApproved}
              disabled={downloadingApproved || statusCounts.approved === 0}
              className="text-green-600 hover:text-green-700"
              data-testid="download-approved-btn"
            >
              {downloadingApproved ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              Download Approved ({statusCounts.approved})
            </Button>
            <Link to="/templates" data-testid="new-form-btn">
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="mr-2 h-4 w-4" />
                New Form
              </Button>
            </Link>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {Object.entries(statusCounts).map(([status, count]) => {
            const config = statusConfig[status] || { label: 'All', class: 'bg-slate-100 text-slate-700' };
            const Icon = config.icon || FileText;
            return (
              <Card 
                key={status}
                className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === status ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setStatusFilter(status)}
                data-testid={`filter-${status}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        {status === 'all' ? 'Total' : config.label}
                      </p>
                      <p className="text-2xl font-bold mt-1">{count}</p>
                    </div>
                    {status !== 'all' && <Icon className="h-5 w-5 text-muted-foreground" />}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search forms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={tradeFilter} onValueChange={setTradeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="trade-filter">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Filter by trade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Trades</SelectItem>
              {getUniqueTradeTypes().map(trade => (
                <SelectItem key={trade} value={trade}>{trade}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="site-filter">
              <Building2 className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Filter by site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sites</SelectItem>
              {getUniqueSites().map(site => (
                <SelectItem key={site} value={site}>{site}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-none"
              data-testid="list-view-btn"
              title="List View"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'trades' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('trades')}
              className="rounded-none"
              data-testid="trade-view-btn"
              title="Group by Trade"
            >
              <Briefcase className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'sites' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('sites')}
              className="rounded-none"
              data-testid="site-view-btn"
              title="Group by Site"
            >
              <Building2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Forms List */}
        {filteredForms.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">No forms found</h3>
              <p className="text-muted-foreground mb-4">
                {forms.length === 0 
                  ? "Get started by creating your first QA form"
                  : "Try adjusting your search or filters"
                }
              </p>
              {forms.length === 0 && (
                <Link to="/templates">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Form
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'trades' ? (
          /* Folder View - Grouped by Trade */
          <div className="space-y-6">
            {Object.entries(getFormsByTrade()).map(([trade, tradeForms]) => (
              <TradeFolderCard 
                key={trade} 
                trade={trade} 
                forms={tradeForms} 
                isAdminOrManager={isAdminOrManager}
                navigate={navigate}
                statusConfig={statusConfig}
              />
            ))}
          </div>
        ) : viewMode === 'sites' ? (
          /* Site View - Grouped by Construction Site */
          <div className="space-y-6">
            {Object.entries(getFormsBySite()).map(([site, siteForms]) => (
              <SiteFolderCard 
                key={site} 
                site={site} 
                forms={siteForms} 
                isAdminOrManager={isAdminOrManager}
                navigate={navigate}
                statusConfig={statusConfig}
                handleDeleteClick={handleDeleteClick}
              />
            ))}
          </div>
        ) : (
          /* List View */
          <div className="grid gap-4">
            {filteredForms.map(form => {
              const config = statusConfig[form.status];
              const StatusIcon = config.icon;
              const canEdit = isAdminOrManager() || (form.status === 'draft' || form.status === 'returned');
              
              return (
                <Card 
                  key={form.form_id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(canEdit ? `/forms/${form.form_id}/edit` : `/forms/${form.form_id}`)}
                  data-testid={`form-card-${form.form_id}`}
                >
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg font-semibold truncate">
                            {form.company_name || form.project_name}
                          </h3>
                          <Badge className={config.class}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {config.label}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{form.trade_type}</span>
                          {form.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {form.location}
                            </span>
                          )}
                          <span>by {form.created_by_name}</span>
                          <span>{format(new Date(form.updated_at), 'MMM d, yyyy h:mm a')}</span>
                        </div>
                        {form.return_reason && (
                          <p className="mt-2 text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md inline-block">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />
                            {form.return_reason}
                          </p>
                        )}
                        {form.reject_reason && (
                          <p className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-md inline-block">
                            <XCircle className="h-3 w-3 inline mr-1" />
                            {form.reject_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdminOrManager() && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => handleDeleteClick(e, form)}
                            data-testid={`delete-btn-${form.form_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-primary">
                          {canEdit ? 'Edit' : 'View'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Form
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{formToDelete?.company_name || formToDelete?.project_name}"? This action cannot be undone. 
              All data, images, and signatures associated with this form will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete Form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
